import React, { useState } from 'react';
import { Button, Spinner, Alert, Table, Input, Label, FormGroup, FormText } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import { DataSourceDifferencesModal } from './dialogs/DataSourceDifferencesModal';
import { ExtraDbDatesModal } from './dialogs/ExtraDbDatesModal';

// Default tolerance (0.5%) used by the backend when no value is supplied.
// Mirrored here so the input below shows the same value out of the box and
// so any caller can rely on it without re-declaring the magic number.
const DEFAULT_TOLERANCE = 0.005;

export const AdminModelsAnalysis = () => {
  const [models, setModels] = useState([]);
  const [uniqueDataSources, setUniqueDataSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyzed, setAnalyzed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [selectedCustomDataSource, setSelectedCustomDataSource] = useState(null);
  const [extraDbDatesModalDs, setExtraDbDatesModalDs] = useState(null);
  const [analysisPackageStatus, setAnalysisPackageStatus] = useState(null);
  const [regeneratingAnalysis, setRegeneratingAnalysis] = useState(false);

  // Parameters pane state — for now only the comparison tolerance, but the
  // pattern is in place to grow it (e.g. sample sizes, parallelism, etc.).
  const [tolerance, setTolerance] = useState(DEFAULT_TOLERANCE);
  const [toleranceInput, setToleranceInput] = useState(String(DEFAULT_TOLERANCE));

  // Clamp to a small positive range; the backend already sanitizes, but we
  // surface a friendly error here instead of silently sending a bad value.
  const parseTolerance = (raw) => {
    if (raw === null || raw === undefined || raw === '') return { ok: false, reason: 'empty' };
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return { ok: false, reason: 'not-a-number' };
    if (parsed < 0) return { ok: false, reason: 'negative' };
    return { ok: true, value: parsed };
  };

  const handleToleranceBlur = () => {
    const parsed = parseTolerance(toleranceInput);
    if (!parsed.ok) {
      // Restore the last known good value so the UI never shows garbage.
      setToleranceInput(String(tolerance));
      return;
    }
    setTolerance(parsed.value);
    setToleranceInput(String(parsed.value));
  };

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleCustomModal = () => setCustomModalOpen(!customModalOpen);

  const refreshAnalysisPackageStatus = async () => {
    try {
      const status = await apiClient.getAnalysisPackageStatus();
      setAnalysisPackageStatus(status);
    } catch (exc) {
      console.warn('Failed to fetch analysis package status:', exc);
    }
  };

  const handleRegenerateAnalysisPackage = async () => {
    try {
      setRegeneratingAnalysis(true);
      setError(null);
      await apiClient.regenerateAnalysisPackage();
      await refreshAnalysisPackageStatus();
    } catch (exc) {
      console.error('Failed to regenerate analysis package:', exc);
      setError(exc.message || 'Failed to regenerate analysis package');
    } finally {
      setRegeneratingAnalysis(false);
    }
  };

  const renderMatchedVia = (matchedVia, matchedTicker) => {
    if (!matchedTicker) {
      return <span className="text-muted small">—</span>;
    }
    if (matchedVia === 'PackageTickers') {
      return (
        <span
          title="Matched via the PackageTickers admin table (authoritative)"
        >
          Package Tickers
        </span>
      );
    }
    return <span className="text-muted small">—</span>;
  };

  const handleShowDifferences = (ds) => {
    setSelectedDataSource(ds);
    setModalOpen(true);
  };

  const handleShowCustomTableDifferences = (ds) => {
    setSelectedCustomDataSource(ds);
    setCustomModalOpen(true);
  };

  const openExtraDbDatesModal = (ds) => {
    setExtraDbDatesModalDs(ds);
  };

  const closeExtraDbDatesModal = () => {
    setExtraDbDatesModalDs(null);
  };

  // Triggered by the modal after a successful delete. When the count drops
  // to zero we close the modal automatically so admins don't have to chase
  // a now-empty dialog. Otherwise we re-run the analysis so the badge and
  // count on the parent panel stay in sync with what's on disk.
  const handleExtraDbDatesChanged = async () => {
    if (!extraDbDatesModalDs || !extraDbDatesModalDs.MatchedTicker) return;
    try {
      const fresh = await apiClient.analyzeModels(tolerance);
      setModels(fresh.Models || []);
      setUniqueDataSources(fresh.UniqueDataSources || []);
      const updated = (fresh.UniqueDataSources || []).find(
        (u) => u.Subfolder === extraDbDatesModalDs.Subfolder && u.TickerName === extraDbDatesModalDs.TickerName
      );
      if (updated && (!updated.CandleComparison || !updated.CandleComparison.HasExtraDbDates)) {
        setExtraDbDatesModalDs(null);
      } else if (updated) {
        setExtraDbDatesModalDs(updated);
      }
    } catch (exc) {
      console.warn('Failed to refresh models analysis after extra-dates delete:', exc);
    }
  };

  const handleAnalyze = async () => {
    // Snap the committed tolerance from the (possibly unblurred) input so
    // users do not have to tab out before clicking Analyze.
    const parsed = parseTolerance(toleranceInput);
    if (!parsed.ok) {
      setError('Data comparison tolerance must be a non-negative number.');
      return;
    }
    const effectiveTolerance = parsed.value;
    setTolerance(effectiveTolerance);
    setToleranceInput(String(effectiveTolerance));

    try {
      setLoading(true);
      setError(null);
      setAnalyzed(false);
      const data = await apiClient.analyzeModels(effectiveTolerance);
      setModels(data.Models || []);
      setUniqueDataSources(data.UniqueDataSources || []);
      setAnalyzed(true);
      await refreshAnalysisPackageStatus();
    } catch (exc) {
      console.error('Failed to analyze models:', exc);
      setError(exc.message || 'Failed to analyze models');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Models Analysis</h2>
        {uniqueDataSources.length > 0 && (
          <a
            href="#unique-data-sources"
            className="small"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById('unique-data-sources');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Jump to unique data sources
          </a>
        )}
      </div>

      <div>
        <div className="parameters-pane mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <h5 className="mb-3">Parameters</h5>
          <FormGroup className="mb-0" style={{ maxWidth: '420px' }}>
            <Label for="data-comparison-tolerance" className="mb-1">
              Data comparison tolerance
              <span className="text-muted ms-2" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                (default {DEFAULT_TOLERANCE}, i.e. 0.5%)
              </span>
            </Label>
            <Input
              id="data-comparison-tolerance"
              type="number"
              step="0.0001"
              min="0"
              value={toleranceInput}
              onChange={(e) => setToleranceInput(e.target.value)}
              onBlur={handleToleranceBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleToleranceBlur();
                }
              }}
              disabled={loading}
              aria-describedby="data-comparison-tolerance-help"
            />
            <FormText id="data-comparison-tolerance-help" color="muted">
              Relative tolerance (fraction) used when comparing DB package rows
              (the Analysis folder CSV mirror) against the original CSV package
              for both standard tickers and special (custom) tables. A value of <code>0.005</code> treats
              any two values whose relative difference
              is at most 0.5% as equal.
            </FormText>
          </FormGroup>
        </div>

        <div className="mb-3">
          <Button color="primary" onClick={handleAnalyze} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Analyzing...
              </>
            ) : (
              'Analyze'
            )}
          </Button>
        </div>

        {error && (
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
        )}

        {analyzed && models.length === 0 && (
          <Alert color="info" fade={false}>
            No models found.
          </Alert>
        )}

        {models.length > 0 && (
          <div className="models-list">
            {models.map((model) => (
              <div key={model.Id} className="mb-4 pb-4 border-bottom">
                <h2>#{model.Id} {model.Name}</h2>
                <div><strong>Filename:</strong> {model.FileName || 'N/A'}</div>
                <div className="mb-3"><strong>Visibility:</strong> {model.Public ? 'Public' : 'Private'}</div>

                {model.Error && (
                  <Alert color="danger" className="mt-2" fade={false}>
                    <strong>Error:</strong> {model.Error}
                  </Alert>
                )}

                {model.DataSources && model.DataSources.length > 0 && (
                  <div className="mt-3">
                    <h5>Data Sources:</h5>
                    <Table striped bordered size="sm" className="table-responsive">
                      <thead>
                        <tr>
                          <th style={{ width: '300px' }}>Folder</th>
                          <th style={{ width: '120px' }}>Subfolder</th>
                          <th>Ticker Name</th>
                          <th style={{ width: '80px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {model.DataSources.map((ds, idx) => {
                          const udsIdx = uniqueDataSources.findIndex(
                            (u) => u.Subfolder === ds.Subfolder && u.TickerName === ds.TickerName
                          );
                          return (
                            <tr key={idx}>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                                {ds.Folder}
                              </td>
                              <td><strong>{ds.Subfolder}</strong></td>
                              <td>{ds.TickerName}</td>
                              <td className="text-center">
                                {udsIdx !== -1 && (
                                  <a
                                    href={`#ds-detail-${udsIdx}`}
                                    className="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const el = document.getElementById(`ds-detail-${udsIdx}`);
                                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }}
                                  >
                                    Jump to
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!model.Error && (!model.DataSources || model.DataSources.length === 0) && (
                  <div className="mt-2 text-muted">
                    <em>No data sources found in this model</em>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {uniqueDataSources.length > 0 && (
          <div className="mt-5">
            <h2 id="unique-data-sources">Unique Data Sources</h2>
            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex flex-wrap align-items-center">
                <strong className="me-3">Analysis package:</strong>
                {analysisPackageStatus?.IsGenerated ? (
                  <span className="text-success me-3">
                    Generated at{' '}
                    {analysisPackageStatus.GeneratedAt
                      ? new Date(analysisPackageStatus.GeneratedAt).toLocaleString()
                      : 'unknown time'}
                    {analysisPackageStatus.Folder && (
                      <span className="ms-2 text-muted" style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                        {analysisPackageStatus.Folder}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-warning me-3">
                    Not generated yet — comparisons for special tables will be skipped until the package is regenerated.
                  </span>
                )}
                <Button
                  color="secondary"
                  size="sm"
                  className="ms-auto"
                  onClick={handleRegenerateAnalysisPackage}
                  disabled={regeneratingAnalysis}
                >
                  {regeneratingAnalysis ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Regenerating...
                    </>
                  ) : (
                    'Regenerate Analysis Package'
                  )}
                </Button>
              </div>
              <div className="text-muted mt-2" style={{ fontSize: '0.85em' }}>
                The Analysis package is a CSV mirror of the current CTRAC package under <code>LocalStorage.Path/Analysis/</code>.
                Both standard tickers and special (custom) tables are compared value-by-value against the uploaded original CSV package.
              </div>
            </div>
            <p className="text-muted">Based on Subfolder + Ticker Name combinations</p>
            <Table striped bordered size="sm" className="table-responsive">
              <thead>
                <tr>
                  <th style={{ width: '300px' }}>Folder</th>
                  <th style={{ width: '120px' }}>Subfolder</th>
                  <th>Ticker Name</th>
                  <th style={{ width: '140px' }}>Matched via</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {uniqueDataSources.map((ds, idx) => (
                  <tr key={idx}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{ds.Folder}</td>
                    <td><strong>{ds.Subfolder}</strong></td>
                    <td>
                      {ds.TickerName}
                      {ds.IsCustomTable && (
                        <span className="badge badge-info ms-1" style={{ fontSize: '0.75em' }}>Special</span>
                      )}
                    </td>
                    <td>{renderMatchedVia(ds.MatchedVia, ds.MatchedTicker)}</td>
                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      <a
                        href={`#ds-detail-${idx}`}
                        className="small"
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(`ds-detail-${idx}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        Jump to
                      </a>
                      {ds.MatchedTicker?.Id && (
                        <>
                          {' · '}
                          <a href={`/candles/ticker/${ds.MatchedTicker.Id}`} className="small">
                            Show candles
                          </a>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="mt-4">
              {uniqueDataSources.map((ds, idx) => (
                <div key={idx} id={`ds-detail-${idx}`} className="mb-4">
                  <h3>
                    {ds.Subfolder} - {ds.TickerName}
                    {ds.IsCustomTable && (
                      <span style={{ fontSize: '0.65em', verticalAlign: 'middle', marginLeft: '1em' }}>Special Table</span>
                    )}
                  </h3>
                  {ds.MatchedTicker ? (
                    <div className="ms-3 text-muted" style={{ fontSize: '0.95em' }}>
                      <div><strong>Ticker ID:</strong> {ds.MatchedTicker.Id}{' '}(<a href={`/candles/ticker/${ds.MatchedTicker.Id}`} className="small">Show candles</a>)</div>
                      <div><strong>Source:</strong> {ds.MatchedTicker.Source}</div>
                      <div><strong>Code:</strong> {ds.MatchedTicker.Code}</div>
                      <div><strong>Name:</strong> {ds.MatchedTicker.Name}</div>
                      <div>
                        <strong>Original package file:</strong>{' '}
                        {ds.OriginalPackageFilePath ? (
                          <span className="text-success">{ds.OriginalPackageFilePath}</span>
                        ) : (
                          <span className="text-danger">Not found</span>
                        )}
                      </div>
                      <div className="mt-2">
                        <strong>DB package rows:</strong>{' '}
                        {ds.DatabaseFirstCandleDate && ds.DatabaseLastCandleDate ? (
                          <span>
                            {new Date(ds.DatabaseFirstCandleDate).toLocaleDateString()} - {new Date(ds.DatabaseLastCandleDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-warning">No rows found</span>
                        )}
                      </div>
                      <div>
<strong>Original package rows:</strong>{' '}
                        {ds.OriginalPackageFirstCandleDate && ds.OriginalPackageLastCandleDate ? (
                          <span>
                            {new Date(ds.OriginalPackageFirstCandleDate).toLocaleDateString()} - {new Date(ds.OriginalPackageLastCandleDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-warning">No rows found</span>
                        )}
                      </div>

                      {ds.CandleComparison && (
                        <div className="mt-3">
                          <strong>Row Comparison:</strong>
                          {ds.CandleComparison.HasDifferences ? (
                            <div className="mt-2">
                              <div className="text-warning mb-2">
                                Found differences within intersection {new Date(ds.CandleComparison.IntersectionStart).toLocaleDateString()} - {new Date(ds.CandleComparison.IntersectionEnd).toLocaleDateString()}
                                {' '}
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 align-baseline"
                                  onClick={() => handleShowDifferences(ds)}
                                  style={{ textDecoration: 'underline' }}
                                >
                                  (more)
                                </Button>
                              </div>
                              <Table striped bordered size="sm" style={{ fontSize: '0.85em' }}>
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Column</th>
                                    <th>DB Package Value</th>
                                    <th>Original Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ds.CandleComparison.Differences?.map((diff, diffIdx) => (
                                    <tr key={diffIdx}>
                                      <td>{new Date(diff.Date).toLocaleDateString()}</td>
                                      <td>{diff.Field}</td>
                                      <td>{diff.DatabaseValue !== null && diff.DatabaseValue !== undefined ? Number(diff.DatabaseValue).toFixed(2) : 'N/A'}</td>
                                      <td>{diff.OriginalValue !== null && diff.OriginalValue !== undefined ? Number(diff.OriginalValue).toFixed(2) : 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <div className="text-success mt-2">
                              Rows within intersection {new Date(ds.CandleComparison.IntersectionStart).toLocaleDateString()} - {new Date(ds.CandleComparison.IntersectionEnd).toLocaleDateString()} match in both original data package and DB package
                            </div>
                          )}

                          {ds.CandleComparison.Gaps && ds.CandleComparison.Gaps.length > 0 && (
                            <div className="mt-3">
                              <strong className="text-danger">Data Gaps in DB Package:</strong>
                              <div className="text-muted" style={{ fontSize: '0.9em' }}>
                                The following date ranges are present in the original package but missing from the DB package:
                              </div>
                              <ul className="mt-2" style={{ fontSize: '0.9em' }}>
                                {ds.CandleComparison.Gaps.map((gap, gapIdx) => (
                                  <li key={gapIdx}>
                                    {new Date(gap.Start).toLocaleDateString()} - {new Date(gap.End).toLocaleDateString()}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {ds.CandleComparison.HasExtraDbDates && ds.MatchedTicker && (
                            <div className="mt-3">
                              <strong className="text-danger">Extra DB Dates:</strong>{' '}
                              <span className="text-muted" style={{ fontSize: '0.9em' }}>
                                {ds.CandleComparison.ExtraDbDateCount} candle date(s) found in the live DB within{' '}
                                {ds.CandleComparison.ExtraDbDatesIntersectionStart ? new Date(ds.CandleComparison.ExtraDbDatesIntersectionStart).toLocaleDateString() : '?'} &ndash;{' '}
                                {ds.CandleComparison.ExtraDbDatesUpperBound ? new Date(ds.CandleComparison.ExtraDbDatesUpperBound).toLocaleDateString() : '?'}{' '}
                                are missing from the original package.
                              </span>{' '}
                              <Button
                                color="warning"
                                size="sm"
                                outline
                                onClick={() => openExtraDbDatesModal(ds)}
                                style={{ marginLeft: '0.25rem' }}
                              >
                                Show extra DB dates
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="ms-3" style={{ fontSize: '0.9em' }}>
                      {ds.IsCustomTable ? (
                        <div>
                          <div className="mt-1">
                            <strong>Type:</strong> Special / Custom table (non-standard columns)
                          </div>
                          {ds.CustomTableColumns && ds.CustomTableColumns.length > 0 && (
                            <div className="mt-1">
                              <strong>Columns:</strong>{' '}
                              <span className="text-muted">{ds.CustomTableColumns.join(', ')}</span>
                            </div>
                          )}
                          <div className="mt-1">
                            <strong>DB package rows (special table):</strong>{' '}
                            {ds.CustomTableFirstDate && ds.CustomTableLastDate ? (
                              <span className="text-success">
                                {new Date(ds.CustomTableFirstDate).toLocaleDateString()} &ndash; {new Date(ds.CustomTableLastDate).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-warning">No data</span>
                            )}
                          </div>
                          <div className="mt-1">
                            <strong>Original package file:</strong>{' '}
                            {ds.OriginalPackageFilePath ? (
                              <span className="text-success">{ds.OriginalPackageFilePath}</span>
                            ) : (
                              <span className="text-danger">Not found in original package</span>
                            )}
                          </div>
                          {ds.OriginalPackageFilePath && (
                            <div className="mt-1">
                              <strong>Original package rows:</strong>{' '}
                              {ds.CustomTableOriginalCsvFirstDate && ds.CustomTableOriginalCsvLastDate ? (
                                <span>
                                  {new Date(ds.CustomTableOriginalCsvFirstDate).toLocaleDateString()} &ndash; {new Date(ds.CustomTableOriginalCsvLastDate).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-warning">No data in CSV</span>
                              )}
                            </div>
                          )}
                          {ds.GeneratedPackageFilePath && (
                            <div className="mt-1">
                              <strong>DB package rows (Analysis folder):</strong>{' '}
                              {ds.CustomTableGeneratedFirstDate && ds.CustomTableGeneratedLastDate ? (
                                <span>
                                  {new Date(ds.CustomTableGeneratedFirstDate).toLocaleDateString()} &ndash; {new Date(ds.CustomTableGeneratedLastDate).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-warning">No data in CSV</span>
                              )}
                            </div>
                          )}

                          {ds.CustomTableComparison ? (
                            <div className="mt-3">
                              <strong>Row Comparison:</strong>
                              {ds.CustomTableComparison.HasDifferences ? (
                                <div className="mt-2">
                                  <div className="text-warning mb-2">
                                    Found {ds.CustomTableComparison.TotalDifferences} value difference(s) within intersection{' '}
                                    {new Date(ds.CustomTableComparison.IntersectionStart).toLocaleDateString()} &ndash;{' '}
                                    {new Date(ds.CustomTableComparison.IntersectionEnd).toLocaleDateString()}
                                    {' '}
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0 align-baseline"
                                      onClick={() => handleShowCustomTableDifferences(ds)}
                                      style={{ textDecoration: 'underline' }}
                                    >
                                      (more)
                                    </Button>
                                  </div>
                                  {ds.CustomTableComparison.Differences && ds.CustomTableComparison.Differences.length > 0 && (
                                    <Table striped bordered size="sm" style={{ fontSize: '0.85em' }}>
                                      <thead>
                                        <tr>
                                          <th>Date</th>
                                          <th>Column</th>
                                          <th>DB Package Value</th>
                                          <th>Original Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {ds.CustomTableComparison.Differences.map((diff, diffIdx) => (
                                          <tr key={diffIdx}>
                                            <td>{new Date(diff.Date).toLocaleDateString()}</td>
                                            <td>{diff.Field}</td>
                                            <td>{diff.DatabaseValue !== null && diff.DatabaseValue !== undefined ? Number(diff.DatabaseValue).toFixed(4).replace(/\.?0+$/, '') : 'N/A'}</td>
                                            <td>{diff.OriginalValue !== null && diff.OriginalValue !== undefined ? Number(diff.OriginalValue).toFixed(4).replace(/\.?0+$/, '') : 'N/A'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  )}
                                </div>
                              ) : (
                                <div className="text-success mt-2">
                                  Rows within intersection{' '}
                                  {new Date(ds.CustomTableComparison.IntersectionStart).toLocaleDateString()} &ndash;{' '}
                                  {new Date(ds.CustomTableComparison.IntersectionEnd).toLocaleDateString()}{' '}
                                  match in both original data package and DB package.
                                </div>
                              )}

                              {ds.CustomTableComparison.Gaps && ds.CustomTableComparison.Gaps.length > 0 && (
                                <div className="mt-3">
                                  <strong className="text-danger">Data Gaps in DB Package:</strong>
                                  <div className="text-muted" style={{ fontSize: '0.9em' }}>
                                    The following date ranges are present in the original CSV package but missing from the DB package:
                                  </div>
                                  <ul className="mt-2" style={{ fontSize: '0.9em' }}>
                                    {ds.CustomTableComparison.Gaps.map((gap, gapIdx) => (
                                      <li key={gapIdx}>
                                        {new Date(gap.Start).toLocaleDateString()} - {new Date(gap.End).toLocaleDateString()}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ) : (
                            ds.OriginalPackageFilePath && (
                              <div className="mt-2 text-muted" style={{ fontSize: '0.9em' }}>
                                <em>
                                  Note: Comparison was skipped because the Analysis folder does not yet contain a generated CSV for this ticker.
                                  Re-run the analysis (or regenerate the Analysis package below) to populate it.
                                </em>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-danger">
                          <em>No matching ticker found</em>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedDataSource && (
          <DataSourceDifferencesModal
            kind="candle"
            isOpen={modalOpen}
            toggle={toggleModal}
            subfolder={selectedDataSource.Subfolder}
            tickerCode={selectedDataSource.MatchedTicker?.Code}
            tickerId={selectedDataSource.MatchedTicker?.Id}
            tickerName={selectedDataSource.TickerName}
            intersectionStart={selectedDataSource.CandleComparison?.IntersectionStart}
            intersectionEnd={selectedDataSource.CandleComparison?.IntersectionEnd}
            tolerance={tolerance}
          />
        )}

        {selectedCustomDataSource && (
          <DataSourceDifferencesModal
            kind="custom"
            isOpen={customModalOpen}
            toggle={toggleCustomModal}
            subfolder={selectedCustomDataSource.Subfolder || selectedCustomDataSource.Folder}
            tickerCode={selectedCustomDataSource.TickerCode}
            tickerName={selectedCustomDataSource.TickerName || selectedCustomDataSource.TickerCode}
            intersectionStart={selectedCustomDataSource.CustomTableComparison?.IntersectionStart}
            intersectionEnd={selectedCustomDataSource.CustomTableComparison?.IntersectionEnd}
            tolerance={tolerance}
          />
        )}

        {extraDbDatesModalDs && extraDbDatesModalDs.MatchedTicker && (
          <ExtraDbDatesModal
            isOpen={!!extraDbDatesModalDs}
            toggle={closeExtraDbDatesModal}
            subfolder={extraDbDatesModalDs.Subfolder}
            tickerCode={extraDbDatesModalDs.MatchedTicker?.Code}
            tickerId={extraDbDatesModalDs.MatchedTicker?.Id}
            tickerName={extraDbDatesModalDs.TickerName}
            onDatesChanged={handleExtraDbDatesChanged}
          />
        )}
      </div>
    </div>
  );
};
